import { useAnalyticsTransport } from '@/viewmodel/features/analytics/hooks/useAnalyticsTransport';
import { useCallback } from 'react';

export function useAnalyticsTracker() {
  const trackWithBackend = useAnalyticsTransport();

  const trackUserRegistered = useCallback(
    (properties: { source: string; method: 'email' | 'oauth'; referral_code?: string }) => {
      return trackWithBackend('user-registered', properties);
    },
    [trackWithBackend],
  );

  const trackWorkspaceAccessed = useCallback(
    (workspaceAccessId: string) => {
      return trackWithBackend('workspace-accessed', {
        workspace_id: workspaceAccessId,
      });
    },
    [trackWithBackend],
  );

  const trackProjectCreated = useCallback(
    (projectId: string, projectWorkspaceId: string) => {
      return trackWithBackend('project-created', {
        project_id: projectId,
        workspace_id: projectWorkspaceId,
      });
    },
    [trackWithBackend],
  );

  const trackDataProcessingCompleted = useCallback(
    (
      sourceId: string,
      layersGenerated: 'bronze' | 'silver' | 'bronze/silver',
      timeToCompleteMs: number,
    ) => {
      return trackWithBackend('data-processing-completed', {
        source_id: sourceId,
        layers_generated: layersGenerated,
        time_to_complete_ms: timeToCompleteMs,
      });
    },
    [trackWithBackend],
  );

  const trackGoldTableCreated = useCallback(
    (tableId: string, projectId: string, method: 'ai' | 'manual') => {
      return trackWithBackend('gold-table-created', {
        table_id: tableId,
        project_id: projectId,
        method,
      });
    },
    [trackWithBackend],
  );

  const trackSessionStarted = useCallback(
    (iface: 'app' | 'mcp') => {
      return trackWithBackend('session-started', {
        interface: iface,
      });
    },
    [trackWithBackend],
  );

  const trackDataSourceConnectionFailed = useCallback(
    (stepName: string, errorType: string, errorCode: string, sourceType: string) => {
      return trackWithBackend('datasource-connection-failed', {
        step_name: stepName,
        error_type: errorType,
        error_code: errorCode,
        source_type: sourceType,
      });
    },
    [trackWithBackend],
  );

  const trackDataSourceConnectionCompleted = useCallback(
    (sourceType: string, timeToCompleteMs: number, retryCount: number) => {
      return trackWithBackend('datasource-connection-completed', {
        source_type: sourceType,
        time_to_complete_ms: timeToCompleteMs,
        retry_count: retryCount,
      });
    },
    [trackWithBackend],
  );

  const trackQueryExecuted = useCallback(
    (iface: 'mcp' | 'app', sourceTool: 'claude' | 'chatgpt' | 'other', projectId: string) => {
      return trackWithBackend('query-executed', {
        interface: iface,
        source_tool: sourceTool,
        project_id: projectId,
      });
    },
    [trackWithBackend],
  );

  const trackMCPSessionStarted = useCallback(
    (clientTool: string, mcpWorkspaceId: string) => {
      return trackWithBackend('mcp-session-started', {
        client_tool: clientTool,
        workspace_id: mcpWorkspaceId,
      });
    },
    [trackWithBackend],
  );

  const trackSQLGenerated = useCallback(
    (accepted: boolean, edited: boolean, tableId: string) => {
      return trackWithBackend('sql-generated', {
        accepted,
        edited,
        table_id: tableId,
      });
    },
    [trackWithBackend],
  );

  const trackDatasourceAdded = useCallback(
    (sourceType: string, projectId: string) => {
      return trackWithBackend('datasource-added', {
        source_type: sourceType,
        project_id: projectId,
      });
    },
    [trackWithBackend],
  );

  return {
    trackUserRegistered,
    trackWorkspaceAccessed,
    trackProjectCreated,
    trackDataProcessingCompleted,
    trackGoldTableCreated,
    trackSessionStarted,
    trackDataSourceConnectionFailed,
    trackDataSourceConnectionCompleted,
    trackQueryExecuted,
    trackMCPSessionStarted,
    trackSQLGenerated,
    trackDatasourceAdded,
  };
}
